import { useEffect, useState } from 'react'
import { Tag, Plus, Edit3, Trash2, Search } from 'lucide-react'

interface Brand {
  id: string
  slug: string
  name: string
  logo: string | null
  country: string | null
  foundedYear: number | null
  description: string | null
  isActive: boolean
  sortOrder: number
  tiresCount: number
  patternsCount: number
}

const blank = {
  id: '', slug: '', name: '', logo: '', country: '',
  foundedYear: '', description: '', isActive: true, sortOrder: 0,
}

export function MarcasLlantas() {
  const [items, setItems] = useState<Brand[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<typeof blank | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function load() {
    setLoading(true)
    const r = await fetch(`/api/admin/marcas-llantas?q=${encodeURIComponent(q)}`)
    const d = await r.json()
    setItems(d.items || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function save() {
    setSaving(true); setError('')
    const isEdit = !!editing!.id
    const payload: any = {
      name: editing!.name,
      logo: editing!.logo || null,
      country: editing!.country || null,
      foundedYear: editing!.foundedYear ? parseInt(editing!.foundedYear as any) : null,
      description: editing!.description || null,
      isActive: editing!.isActive,
      sortOrder: parseInt(String(editing!.sortOrder)) || 0,
    }
    if (!isEdit) payload.slug = editing!.slug
    const url = isEdit ? `/api/admin/marcas-llantas/${editing!.id}` : '/api/admin/marcas-llantas'
    const method = isEdit ? 'PATCH' : 'POST'
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    setSaving(false)
    if (!res.ok) {
      const d = await res.json().catch(() => ({}))
      setError(d.error || `HTTP ${res.status}`)
      return
    }
    setEditing(null)
    load()
  }

  async function remove(b: Brand) {
    const msg = b.tiresCount > 0
      ? `${b.name} tiene ${b.tiresCount} llantas. Se marcará como INACTIVA. Continuar?`
      : `Eliminar permanentemente la marca ${b.name}?`
    if (!confirm(msg)) return
    await fetch(`/api/admin/marcas-llantas/${b.id}`, { method: 'DELETE' })
    load()
  }

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-extrabold text-ink-900">
            <Tag className="size-6 text-brand-600" /> Marcas de llantas
          </h1>
          <p className="text-sm text-ink-500">
            {items.length} marcas · Bridgestone, Michelin, Continental y más
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ ...blank })}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
        >
          <Plus className="size-4" /> Nueva marca
        </button>
      </header>

      <div className="rounded-2xl border border-ink-200 bg-white p-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400" />
          <input
            type="search"
            placeholder="Buscar marca…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="w-full rounded-lg border border-ink-200 bg-white pl-9 pr-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-left text-xs font-semibold uppercase tracking-wider text-ink-500">
            <tr>
              <th className="px-4 py-3">Marca</th>
              <th className="px-4 py-3">País</th>
              <th className="px-4 py-3">Fundada</th>
              <th className="px-4 py-3 text-right">Llantas</th>
              <th className="px-4 py-3 text-right">Líneas</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-ink-400">Cargando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-ink-400">Sin marcas</td></tr>
            ) : items.map((b) => (
              <tr key={b.id} className="hover:bg-ink-50/50">
                <td className="px-4 py-3">
                  <div className="font-semibold text-ink-900">{b.name}</div>
                  <div className="font-mono text-xs text-ink-500">{b.slug}</div>
                </td>
                <td className="px-4 py-3 text-ink-700">{b.country || '—'}</td>
                <td className="px-4 py-3 text-ink-700">{b.foundedYear || '—'}</td>
                <td className="px-4 py-3 text-right">
                  <span className="rounded-md bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700">{b.tiresCount}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="rounded-md bg-ink-100 px-2 py-0.5 text-xs">{b.patternsCount}</span>
                </td>
                <td className="px-4 py-3">
                  {b.isActive
                    ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Activa</span>
                    : <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs">Inactiva</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing({ ...b, foundedYear: b.foundedYear?.toString() || '', logo: b.logo || '', country: b.country || '', description: b.description || '' } as any)}
                    className="mr-1 inline-flex rounded p-1.5 text-ink-500 hover:bg-brand-50 hover:text-brand-700">
                    <Edit3 className="size-4" />
                  </button>
                  <button onClick={() => remove(b)} className="inline-flex rounded p-1.5 text-ink-500 hover:bg-red-50 hover:text-red-700">
                    <Trash2 className="size-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal edición */}
      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6">
            <h2 className="mb-4 text-lg font-extrabold text-ink-900">
              {editing.id ? 'Editar marca' : 'Nueva marca'}
            </h2>
            <form onSubmit={(e) => { e.preventDefault(); save() }} className="space-y-3">
              {!editing.id && (
                <Field label="Slug (kebab-case)">
                  <input required pattern="[a-z0-9-]+" value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    placeholder="bridgestone" className="form-input" />
                </Field>
              )}
              <Field label="Nombre">
                <input required value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Bridgestone" className="form-input" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="País">
                  <input value={editing.country}
                    onChange={(e) => setEditing({ ...editing, country: e.target.value })}
                    placeholder="Japón" className="form-input" />
                </Field>
                <Field label="Año fundación">
                  <input type="number" value={editing.foundedYear}
                    onChange={(e) => setEditing({ ...editing, foundedYear: e.target.value })}
                    placeholder="1931" className="form-input" />
                </Field>
              </div>
              <Field label="URL logo">
                <input value={editing.logo}
                  onChange={(e) => setEditing({ ...editing, logo: e.target.value })}
                  placeholder="https://..." className="form-input" />
              </Field>
              <Field label="Descripción">
                <textarea rows={3} value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="form-input" />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.isActive}
                  onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} />
                Activa
              </label>

              {error && <div className="rounded-lg bg-red-50 p-2 text-sm text-red-700">{error}</div>}

              <div className="flex justify-end gap-2 pt-3">
                <button type="button" onClick={() => setEditing(null)}
                  className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-semibold">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-ink-700">{label}</span>
      {children}
    </label>
  )
}
